import React, { useState, useEffect, useCallback, createContext, useContext } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Paper,
  Card,
  CardContent,
  Chip,
  Grid,
  Fab,
  Slide,
  Fade,
  IconButton,
  Tooltip,
  Switch,
  FormControlLabel,
  useTheme,
  ThemeProvider,
  createTheme,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import {
  PlayArrow,
  Pause,
  SkipNext,
  Autorenew,
  Stop,
  Videocam,
  Favorite,
  Share,
  DarkMode,
  LightMode,
  HelpOutline,
  Close,
  Psychology,
  GridView,
} from '@mui/icons-material';
import { styled, keyframes } from '@mui/material/styles';
import VideoPlayer from './components/VideoPlayer';
import VideoInfo from './components/VideoInfo';
import StatsCard from './components/StatsCard';
import SmartPlaylist from './components/SmartPlaylist';
import VideoGallery from './components/VideoGallery';
import NextVlogPreview from './components/NextVlogPreview';
import { videoIds, getVideoIds, getVideoMetadata, getVideoStats, markVideoAsPlayed, getPlayStatistics, clearPlayHistory, debugVideoWeights, getWeightedRandomVideo, getAvailableEras } from './data/videoData';

// Backup video IDs that are known to work reliably
const FALLBACK_VIDEOS = [
  '29_omo0QGXw', // KISSING A GIRL FOR THE FIRST TIME !!
  'hbAnW2MhExc', // SURPRISE DREAM VACATION !!
  'S8nNhY-XVXU', // SURPRISING FRIENDS WITH BODY TRANSFORMATION!!
  '-b1WnjKvcUg', // HOT TUB WITH INTERNATIONAL MODEL!!
  '-bpkiObJMCY', // SURPRISE MADE HER START CRYING!!
];

// Helper function to parse YouTube duration format (PT7M21S -> 7:21)
const parseDuration = (duration) => {
  if (!duration) return 'Unknown';
  
  const match = duration.match(/PT(?:(\d+)H)?(?:(\d+)M)?(?:(\d+)S)?/);
  if (!match) return 'Unknown';
  
  const hours = parseInt(match[1] || 0);
  const minutes = parseInt(match[2] || 0);
  const seconds = parseInt(match[3] || 0);
  
  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  } else {
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  }
};

// Create theme context
const ThemeContext = createContext();

// YoungLA-inspired theme
const createAppTheme = (mode) => createTheme({
  palette: {
    mode,
    primary: {
      main: mode === 'dark' ? '#FFFFFF' : '#000000',
      light: mode === 'dark' ? '#F5F5F5' : '#333333', 
      dark: mode === 'dark' ? '#E0E0E0' : '#000000',
      contrastText: mode === 'dark' ? '#000000' : '#FFFFFF',
    },
    secondary: {
      main: mode === 'dark' ? '#FF6B6B' : '#FF5252',
      light: mode === 'dark' ? '#FF8A80' : '#FF7043',
      dark: mode === 'dark' ? '#E53935' : '#D32F2F',
    },
    background: {
      default: mode === 'dark' ? '#0A0A0A' : '#F8F9FA',
      paper: mode === 'dark' ? '#1A1A1A' : '#FFFFFF',
    },
    text: {
      primary: mode === 'dark' ? '#FFFFFF' : '#000000',
      secondary: mode === 'dark' ? '#B0B0B0' : '#666666',
    },
    divider: mode === 'dark' ? '#2A2A2A' : '#E0E0E0',
    // Custom YoungLA colors
    youngla: {
      accent: mode === 'dark' ? '#FF6B6B' : '#FF5252',
      silver: mode === 'dark' ? '#C0C0C0' : '#9E9E9E',
      gold: mode === 'dark' ? '#FFD700' : '#FFC107',
      charcoal: mode === 'dark' ? '#2A2A2A' : '#424242',
    }
  },
  typography: {
    fontFamily: "'Inter', 'SF Pro Display', -apple-system, BlinkMacSystemFont, sans-serif",
    h1: {
      fontWeight: 700,
      letterSpacing: '-0.025em',
      lineHeight: 1.1,
    },
    h2: {
      fontWeight: 600,
      letterSpacing: '-0.02em',
      lineHeight: 1.2,
    },
    h3: {
      fontWeight: 600,
      letterSpacing: '-0.015em',
      lineHeight: 1.3,
    },
    h6: {
      fontWeight: 600,
      letterSpacing: '-0.01em',
    },
    body1: {
      fontWeight: 400,
      letterSpacing: '-0.01em',
      lineHeight: 1.6,
    },
    body2: {
      fontWeight: 400,
      letterSpacing: '-0.005em',
      lineHeight: 1.5,
    },
    button: {
      fontWeight: 600,
      letterSpacing: '0.01em',
      textTransform: 'none',
    }
  },
  shape: {
    borderRadius: 8,
  },
  components: {
    MuiButton: {
      styleOverrides: {
        root: ({ theme, ownerState }) => ({
          textTransform: 'none',
          fontWeight: 600,
          borderRadius: 8,
          padding: '12px 24px',
          boxShadow: 'none',
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            boxShadow: 'none',
            transform: 'translateY(-1px)',
          },
          ...(ownerState.variant === 'contained' && {
            background: theme.palette.mode === 'dark' 
              ? 'linear-gradient(135deg, #FFFFFF 0%, #F0F0F0 100%)'
              : 'linear-gradient(135deg, #000000 0%, #333333 100%)',
            color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
            '&:hover': {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #F0F0F0 0%, #E0E0E0 100%)'
                : 'linear-gradient(135deg, #333333 0%, #555555 100%)',
            },
          }),
        })
      }
    },
    MuiCard: {
      styleOverrides: {
        root: ({ theme }) => ({
          background: theme.palette.mode === 'dark' 
            ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)'
            : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
          border: `1px solid ${theme.palette.mode === 'dark' ? '#2A2A2A' : '#E0E0E0'}`,
          borderRadius: 12,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 4px 20px rgba(0, 0, 0, 0.3)'
            : '0 4px 20px rgba(0, 0, 0, 0.08)',
          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-2px)',
            boxShadow: theme.palette.mode === 'dark'
              ? '0 8px 32px rgba(0, 0, 0, 0.4)'
              : '0 8px 32px rgba(0, 0, 0, 0.12)',
          },
        })
      }
    },
    MuiDialog: {
      styleOverrides: {
        paper: ({ theme }) => ({
          background: theme.palette.mode === 'dark'
            ? 'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)'
            : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
          border: `1px solid ${theme.palette.mode === 'dark' ? '#333333' : '#E0E0E0'}`,
          borderRadius: 5,
          boxShadow: theme.palette.mode === 'dark'
            ? '0 24px 64px rgba(0, 0, 0, 0.6)'
            : '0 24px 64px rgba(0, 0, 0, 0.2)',
        })
      }
    },
    MuiIconButton: {
      styleOverrides: {
        root: ({ theme }) => ({
          borderRadius: 8,
          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
          '&:hover': {
            transform: 'translateY(-1px)',
            backgroundColor: theme.palette.mode === 'dark'
              ? 'rgba(255, 255, 255, 0.1)'
              : 'rgba(0, 0, 0, 0.04)',
          },
        })
      }
    }
  }
});

const subtleAnimation = keyframes`
  0%, 100% { 
    background-position: 0% 50%; 
  }
  50% { 
    background-position: 100% 50%; 
  }
`;

const BackgroundContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  backgroundColor: theme.palette.background.default,
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.5s cubic-bezier(0.4, 0, 0.2, 1)',
}));

const CleanCard = styled(Paper)(({ theme }) => ({
  background: theme.palette.mode === 'dark'
    ? 'linear-gradient(135deg, rgba(26, 26, 26, 0.95) 0%, rgba(42, 42, 42, 0.95) 100%)'
    : 'linear-gradient(135deg, rgba(255, 255, 255, 0.95) 0%, rgba(248, 249, 250, 0.95) 100%)',
  borderRadius: 16,
  border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.08)'}`,
  boxShadow: theme.palette.mode === 'dark' 
    ? '0 8px 32px rgba(0, 0, 0, 0.4), 0 1px 0 rgba(255, 255, 255, 0.05) inset, 0 0 0 1px rgba(255, 107, 107, 0.1)'
    : '0 8px 32px rgba(0, 0, 0, 0.1), 0 1px 0 rgba(255, 255, 255, 0.9) inset, 0 0 0 1px rgba(255, 82, 82, 0.05)',
  backdropFilter: 'blur(20px) saturate(120%)',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  overflow: 'hidden',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '1px',
    background: theme.palette.mode === 'dark'
      ? 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.2), transparent)'
      : 'linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.8), transparent)',
  },
  '&:hover': {
    transform: 'translateY(-4px) scale(1.02)',
    boxShadow: theme.palette.mode === 'dark'
      ? '0 16px 48px rgba(0, 0, 0, 0.5), 0 2px 0 rgba(255, 255, 255, 0.08) inset, 0 0 0 1px rgba(255, 107, 107, 0.2)'
      : '0 16px 48px rgba(0, 0, 0, 0.15), 0 2px 0 rgba(255, 255, 255, 0.95) inset, 0 0 0 1px rgba(255, 82, 82, 0.1)',
    border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.15)' : 'rgba(0, 0, 0, 0.1)'}`,
  },
}));

const PulsingIcon = styled(Videocam)(({ theme }) => ({
  animation: 'pulse 2s infinite',
  '@keyframes pulse': {
    '0%': { transform: 'scale(1)' },
    '50%': { transform: 'scale(1.1)' },
    '100%': { transform: 'scale(1)' },
  },
}));

function AppContent() {
  const theme = useTheme();
  const { darkMode, toggleDarkMode } = useContext(ThemeContext);
  
  const [currentVideoId, setCurrentVideoId] = useState('29_omo0QGXw'); // Test with known working video
  const [isPlaying, setIsPlaying] = useState(false);
  const [autoplay] = useState(true); // Autoplay is now permanently enabled
  const [videoCount, setVideoCount] = useState(0);
  const [isLoading, setIsLoading] = useState(false); // Test without loading overlay
  const [isAutoplayLoading, setIsAutoplayLoading] = useState(false);
  const [currentVideoInfo, setCurrentVideoInfo] = useState({
    title: 'Loading...',
    description: 'Getting ready to play some amazing content!',
  });
  const [failedVideos, setFailedVideos] = useState(new Set());
  const [useFallback, setUseFallback] = useState(false);
  const [allVideoIds, setAllVideoIds] = useState(videoIds);
  const [videoStats, setVideoStats] = useState(null);
  const [playStats, setPlayStats] = useState(null);
  const [currentVideoMetadata, setCurrentVideoMetadata] = useState(null);
  const [isComponentMounted, setIsComponentMounted] = useState(false);
  const [userPreferences, setUserPreferences] = useState(() => {
    // Load preferences from localStorage
    const saved = localStorage.getItem('dobrik-user-preferences');
    return saved ? JSON.parse(saved) : {
      preferredEras: [],
      preferredDuration: null,
      avoidRepeats: true,
      watchHistory: [],
      favoriteVideos: []
    };
  });
  const [helpOpen, setHelpOpen] = useState(false);
  const [shouldEnterFullscreen, setShouldEnterFullscreen] = useState(false);
  const [selectedEra, setSelectedEra] = useState('');
  const [availableEras, setAvailableEras] = useState([]);

  const getRandomVideoId = useCallback(() => {
    // Use the loaded video IDs instead of the static list
    let videoList = allVideoIds.length > 0 ? allVideoIds : FALLBACK_VIDEOS;
    
    // Apply user preferences for avoiding repeats
    if (userPreferences.avoidRepeats && userPreferences.watchHistory.length > 0) {
      const unwatchedVideos = videoList.filter(id => !userPreferences.watchHistory.includes(id));
      if (unwatchedVideos.length > 0) {
        videoList = unwatchedVideos;
      }
    }
    
    // If too many videos have failed, switch to fallback videos
    if (failedVideos.size > videoList.length / 2 || useFallback) {
      setUseFallback(true);
      const availableFallbacks = FALLBACK_VIDEOS.filter(id => !failedVideos.has(id));
      
      if (availableFallbacks.length === 0) {
        // Reset failed videos and use first fallback
        setFailedVideos(new Set());
        return FALLBACK_VIDEOS[0];
      }
      
      const randomIndex = Math.floor(Math.random() * availableFallbacks.length);
      return availableFallbacks[randomIndex];
    }
    
    // Filter out failed videos from main list
    const availableVideos = videoList.filter(id => !failedVideos.has(id));
    
    // If all main videos have failed, switch to fallback
    if (availableVideos.length === 0) {
      setUseFallback(true);
      return FALLBACK_VIDEOS[0];
    }
    
    const randomIndex = Math.floor(Math.random() * availableVideos.length);
    return availableVideos[randomIndex];
  }, [failedVideos, useFallback, allVideoIds, userPreferences]);

  const playRandomVideo = useCallback(async () => {
    setIsLoading(true);
    try {
      // Use era-aware weighted random selection if era is selected
      const eraFilter = selectedEra || null;
      const randomVideo = await getWeightedRandomVideo(eraFilter);
      
      if (randomVideo) {
        setCurrentVideoId(randomVideo.id);
        setCurrentVideoInfo({
          title: randomVideo.title || 'Loading next vlog...',
          description: randomVideo.description || 'Getting ready for more awesome content! 🎉',
        });
        
        // Update metadata
        setCurrentVideoMetadata(randomVideo);
      } else {
        // Fallback to old method if no video found
    const newVideoId = getRandomVideoId();
    setCurrentVideoId(newVideoId);
    setCurrentVideoInfo({
      title: 'Loading next vlog...',
      description: 'Getting ready for more awesome content!',
    });
      }
    } catch (error) {
      console.error('Error getting random video:', error);
      // Fallback to old method
      const newVideoId = getRandomVideoId();
      setCurrentVideoId(newVideoId);
      setCurrentVideoInfo({
        title: 'Loading next vlog...',
        description: 'Getting ready for more awesome content!',
      });
    }
  }, [getRandomVideoId, selectedEra]);

  const handleVideoEnd = useCallback((wasInFullscreen = false) => {
    console.log('Video ended. Was in fullscreen:', wasInFullscreen);
    if (autoplay) {
      // Store the fullscreen state for the next video
      setShouldEnterFullscreen(wasInFullscreen);
      setIsAutoplayLoading(true);
      setTimeout(() => {
        playRandomVideo();
        setVideoCount(prev => prev + 1);
        setIsAutoplayLoading(false);
      }, 3000);
    }
  }, [autoplay, playRandomVideo]);

  const handleVideoStart = useCallback(async (videoData) => {
    setIsLoading(false);
    setIsPlaying(true);
    setVideoCount(prev => prev + 1);
    
    // Reset fullscreen flag after video starts
    setShouldEnterFullscreen(false);
    
    // Track video in watch history for smart playlists
    if (currentVideoId && userPreferences.avoidRepeats) {
      const newHistory = [currentVideoId, ...userPreferences.watchHistory.filter(id => id !== currentVideoId)].slice(0, 50);
      const updatedPreferences = { ...userPreferences, watchHistory: newHistory };
      setUserPreferences(updatedPreferences);
      localStorage.setItem('dobrik-user-preferences', JSON.stringify(updatedPreferences));
    }
    
    // Mark video as played in cache and update statistics
    if (currentVideoId) {
      markVideoAsPlayed(currentVideoId);
      // Refresh play statistics
      setPlayStats(getPlayStatistics());
    }
    
    // Try to get metadata for better video info
    try {
      const metadata = await getVideoMetadata(currentVideoId);
      if (metadata) {
        setCurrentVideoMetadata(metadata);
        setCurrentVideoInfo({
          title: metadata.title || 'David Dobrik Vlog',
          description: `${metadata.era} • Published: ${new Date(metadata.publishedAt).toLocaleDateString()} • Duration: ${parseDuration(metadata.duration)}`,
        });
      } else {
        setCurrentVideoInfo({
          title: videoData.title || 'David Dobrik Vlog',
          description: 'Enjoy this awesome David Dobrik vlog!',
        });
      }
    } catch (error) {
      setCurrentVideoInfo({
        title: videoData.title || 'David Dobrik Vlog',
        description: 'Enjoy this awesome David Dobrik vlog! 🎬✨',
      });
    }
  }, [currentVideoId]);

  const handleVideoError = useCallback((errorVideoId) => {
    if (process.env.NODE_ENV === 'development') {
      console.log('Video failed, marking as unavailable:', errorVideoId);
    }
    setFailedVideos(prev => new Set([...prev, errorVideoId]));
    
    // Show user-friendly message
    setCurrentVideoInfo({
      title: 'Oops! That video is unavailable',
              description: 'Don\'t worry, we\'re finding you another awesome vlog!',
    });
    
    // Automatically try next video
    setTimeout(() => {
      setIsLoading(true);
      const newVideoId = getRandomVideoId();
      setCurrentVideoId(newVideoId);
      setCurrentVideoInfo({
        title: 'Loading next vlog...',
        description: 'Getting ready for more amazing content!',
      });
    }, 2000);
  }, [getRandomVideoId]);



  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  // Handle playing a specific video from playlist
  const handlePlaySpecificVideo = useCallback((videoId) => {
    setCurrentVideoId(videoId);
    setCurrentVideoInfo({
      title: 'Loading selected vlog...',
      description: 'Playing your chosen David Dobrik video!',
    });
  }, []);

  // Handle updating user preferences
  const handleUpdatePreferences = useCallback((newPreferences) => {
    setUserPreferences(newPreferences);
    localStorage.setItem('dobrik-user-preferences', JSON.stringify(newPreferences));
  }, []);

  // Load all video IDs and stats on component mount
  useEffect(() => {
    const loadVideoData = async () => {
      try {
        // Load all video IDs from API
        const ids = await getVideoIds();
        setAllVideoIds(ids);
        
        // Load video statistics
        const stats = await getVideoStats();
        setVideoStats(stats);
        
        // Load available eras
        const eras = await getAvailableEras();
        setAvailableEras(eras);
        
        // Initialize play statistics
        const playStatsData = getPlayStatistics();
        setPlayStats(playStatsData);
        
        console.log(`Loaded ${ids.length} videos from David Dobrik's channel`);
        if (playStatsData.totalPlays > 0) {
          console.log(`Play history: ${playStatsData.totalPlays} total plays, ${playStatsData.uniqueVideosPlayed} unique videos (${playStatsData.percentageWatched}% of collection)`);
        }
        
        // Set component as mounted after data loads to prevent animation errors
        setTimeout(() => setIsComponentMounted(true), 150);
        
        // Expose debug functions in development
        if (process.env.NODE_ENV === 'development') {
          window.clearPlayHistory = clearPlayHistory;
          window.debugVideoWeights = debugVideoWeights;
          window.getPlayStatistics = getPlayStatistics;
          console.log('Debug functions available: clearPlayHistory(), debugVideoWeights(), getPlayStatistics()');
        }
        
        // Initialize with the first available video
        const initialVideoId = ids[0] || FALLBACK_VIDEOS[0];
        setCurrentVideoId(initialVideoId);
        setIsLoading(false);
        
        // Try to get metadata for better initial info
        try {
          const metadata = await getVideoMetadata(initialVideoId);
          setCurrentVideoInfo({
            title: metadata?.title || 'David Dobrik Vlog',
            description: 'David Dobrik is back with amazing content!',
          });
        } catch (error) {
          setCurrentVideoInfo({
            title: 'David Dobrik Vlog',
            description: 'David Dobrik is back with amazing content!',
          });
        }
      } catch (error) {
        console.error('Failed to load video data:', error);
        // Set component as mounted even if data loading fails
        setTimeout(() => setIsComponentMounted(true), 150);
        // Fallback to hardcoded videos
        const initialVideoId = FALLBACK_VIDEOS[0];
        setCurrentVideoId(initialVideoId);
        setIsLoading(false);
        setCurrentVideoInfo({
          title: 'David Dobrik Vlog',
          description: 'David Dobrik is back with amazing content!',
        });
      }
    };

    loadVideoData();
  }, []);

  useEffect(() => {
    const handleKeyDown = (e) => {
      // Prevent shortcuts when typing in input fields
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA' || e.target.isContentEditable) {
        return;
      }

      switch (e.code) {
        case 'Space':
          e.preventDefault();
          handlePlayPause();
          break;
        
        case 'ArrowRight':
        case 'KeyN':
          e.preventDefault();
          playRandomVideo();
          break;
        
        case 'ArrowLeft':
        case 'KeyP':
          e.preventDefault();
          playRandomVideo(); // Previous could be implemented later
          break;
        
        case 'KeyF':
          e.preventDefault();
          setShouldEnterFullscreen(true);
          break;
        
        case 'KeyT':
          e.preventDefault();
          toggleDarkMode();
          break;
        

        
        case 'KeyH':
        case 'F1':
          e.preventDefault();
          setHelpOpen(true);
          break;
        
        case 'KeyG':
          e.preventDefault();
          // Trigger video gallery (assuming it has a way to open)
          break;
        
        case 'KeyS':
          e.preventDefault();
          // Trigger smart playlist (assuming it has a way to open)
          break;
        
        case 'KeyR':
          e.preventDefault();
          if (e.ctrlKey || e.metaKey) return; // Don't interfere with browser refresh
          playRandomVideo();
          break;
        
        case 'Escape':
          e.preventDefault();
          // Close any open dialogs
          setHelpOpen(false);
          break;
        
        case 'KeyM':
          e.preventDefault();
          // Mute/unmute (if video player supports it)
          break;
        
        case 'ArrowUp':
          e.preventDefault();
          // Volume up (if implemented)
          break;
        
        case 'ArrowDown':
          e.preventDefault();
          // Volume down (if implemented)
          break;
        
        case 'Digit1':
        case 'Digit2':
        case 'Digit3':
        case 'Digit4':
        case 'Digit5':
          e.preventDefault();
          // Quick video selection (if implemented)
          break;
        
        default:
          break;
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [playRandomVideo, toggleDarkMode]);

  return (
    <BackgroundContainer>
      <Container maxWidth="lg" sx={{ py: 4, position: 'relative', zIndex: 1 }}>
        {/* Top Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          {/* Tip Button */}
          <Button
            variant="contained"
            href="https://www.paypal.com/ncp/payment/KHUZPG7NG73W8"
            target="_blank"
            rel="noopener noreferrer"
            sx={{
              bgcolor: theme.palette.secondary.main,
              color: theme.palette.secondary.contrastText,
              fontWeight: 600,
              fontSize: '0.9rem',
              px: 3,
              py: 1,
              borderRadius: 2,
              boxShadow: `0 4px 16px ${theme.palette.secondary.main}40`,
              transition: 'all 0.3s ease',
              '&:hover': {
                bgcolor: theme.palette.secondary.dark,
                transform: 'translateY(-2px)',
                boxShadow: `0 6px 20px ${theme.palette.secondary.main}60`,
              },
            }}
          >
            💰 Tip for the Chaos!
          </Button>

          {/* Dark Mode Toggle */}
          <IconButton
            onClick={toggleDarkMode}
            sx={{
              bgcolor: theme.palette.background.paper,
              border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.06)'}`,
              '&:hover': {
                bgcolor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.05)' : 'rgba(0, 0, 0, 0.02)',
              }
            }}
          >
            {darkMode ? <LightMode sx={{ color: theme.palette.text.primary }} /> : <DarkMode sx={{ color: theme.palette.text.primary }} />}
          </IconButton>
        </Box>

        {/* Header - Clean and Classy */}
        <Fade in timeout={1000}>
          <Box textAlign="center" mb={4} sx={{ position: 'relative' }}>
              <Typography
                variant="h1"
                sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 50%, #FF6B00 100%)`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                fontWeight: 900,
                fontSize: { xs: '2.8rem', sm: '4.2rem', md: '5.5rem', lg: '6.5rem' },
                letterSpacing: '-0.02em',
                fontFamily: '"Montserrat", "Bebas Neue", "Impact", sans-serif',
                textTransform: 'uppercase',
                lineHeight: 0.85,
                mb: 1,
                position: 'relative',
                textShadow: theme.palette.mode === 'dark' 
                  ? '2px 2px 0px rgba(0,0,0,0.8), 4px 4px 0px rgba(0,0,0,0.4)' 
                  : '2px 2px 0px rgba(255,255,255,0.8), 4px 4px 0px rgba(0,0,0,0.1)',
                '&::before': {
                  content: '"DAVID\'S VLOGS"',
                  position: 'absolute',
                  top: '3px',
                  left: '3px',
                  right: 0,
                  background: theme.palette.mode === 'dark' ? '#000' : '#FFF',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                  backgroundClip: 'text',
                  zIndex: -1,
                  opacity: 0.3,
                },
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  bottom: '-8px',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  width: '60%',
                  height: '4px',
                  background: `linear-gradient(90deg, ${theme.palette.primary.main}, ${theme.palette.secondary.main}, #FF6B00)`,
                  borderRadius: '2px',
                  boxShadow: `0 2px 8px rgba(255, 107, 0, 0.4)`,
                }
              }}
            >
              David's Vlogs
              </Typography>
            <Typography
              variant="subtitle1"
                sx={{ 
                  color: theme.palette.text.secondary,
                  fontWeight: 600,
                fontSize: { xs: '0.9rem', sm: '1.1rem', md: '1.2rem' },
                letterSpacing: '0.1em',
                fontFamily: '"Montserrat", sans-serif',
                textTransform: 'uppercase',
                mt: 2,
                opacity: 0.8,
              }}
            >
              Random Content • Maximum Chaos
            </Typography>
          </Box>
        </Fade>

        <Grid container spacing={3}>
          {/* Video Player */}
          <Grid item xs={12} lg={8}>
            {isComponentMounted ? (
              <Slide direction="up" in timeout={800}>
                <div>
                  <CleanCard elevation={0} sx={{ p: 3, mb: 3 }}>
                    <VideoPlayer
                      videoId={currentVideoId}
                      onEnd={handleVideoEnd}
                      onStart={handleVideoStart}
                      onError={handleVideoError}
                      isPlaying={isPlaying}
                      onPlayingChange={setIsPlaying}
                      videoDuration={currentVideoMetadata?.duration}
                      shouldEnterFullscreen={shouldEnterFullscreen}
                      isAutoplayLoading={isAutoplayLoading}
                    />
                  </CleanCard>
                </div>
              </Slide>
            ) : (
              <CleanCard elevation={0} sx={{ p: 3, mb: 3 }}>
                <VideoPlayer
                  videoId={currentVideoId}
                  onEnd={handleVideoEnd}
                  onStart={handleVideoStart}
                  onError={handleVideoError}
                  isPlaying={isPlaying}
                  onPlayingChange={setIsPlaying}
                  videoDuration={currentVideoMetadata?.duration}
                  shouldEnterFullscreen={shouldEnterFullscreen}
                  isAutoplayLoading={isAutoplayLoading}
                />
              </CleanCard>
            )}

            {/* Video Info - Complete component under the player */}
            {isComponentMounted ? (
              <Slide direction="up" in timeout={900}>
                <div>
                  <VideoInfo
                    title={currentVideoInfo.title}
                    description={currentVideoInfo.description}
                    isLoading={isLoading}
                  />
                </div>
              </Slide>
            ) : (
              <VideoInfo
                title={currentVideoInfo.title}
                description={currentVideoInfo.description}
                isLoading={isLoading}
              />
            )}



            {/* Enhanced Controls */}
          </Grid>

          {/* Sidebar */}
          <Grid item xs={12} lg={4}>
            {isComponentMounted ? (
              <Slide direction="left" in timeout={1200}>
                <div>
                  <Box>
                {/* Quick Actions */}
                <CleanCard elevation={0} sx={{ p: 3 }}>
                  <Typography variant="h6" color={theme.palette.text.primary} gutterBottom textAlign="center" fontWeight={600}>
                    Quick Actions
                  </Typography>
                  
                  {/* Era Filter */}
                  <Box display="flex" justifyContent="center" mb={3}>
                    <FormControl 
                      variant="outlined" 
                      sx={{ 
                        minWidth: 240,
                        '& .MuiOutlinedInput-root': {
                          background: theme.palette.mode === 'dark' 
                            ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)'
                            : 'linear-gradient(135deg, #FFFFFF 0%, #F8F8F8 100%)',
                          borderRadius: 3,
                          border: `2px solid ${theme.palette.mode === 'dark' 
                            ? 'rgba(255, 107, 107, 0.3)' 
                            : 'rgba(255, 82, 82, 0.2)'}`,
                          transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            border: `2px solid ${theme.palette.mode === 'dark' 
                              ? 'rgba(255, 107, 107, 0.6)' 
                              : 'rgba(255, 82, 82, 0.4)'}`,
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 25px rgba(255, 107, 107, 0.3)'
                              : '0 8px 25px rgba(255, 82, 82, 0.2)',
                          },
                          '&.Mui-focused': {
                            border: `2px solid ${theme.palette.mode === 'dark' 
                              ? '#FF6B6B' 
                              : '#FF5252'}`,
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 12px 30px rgba(255, 107, 107, 0.4)'
                              : '0 12px 30px rgba(255, 82, 82, 0.3)',
                          },
                          '& fieldset': { border: 'none' },
                        },
                        '& .MuiInputLabel-root': {
                          color: theme.palette.text.secondary,
                          fontWeight: 600,
                          fontSize: '0.9rem',
                          '&.Mui-focused': {
                            color: theme.palette.mode === 'dark' ? '#FF6B6B' : '#FF5252',
                          }
                        },
                        '& .MuiSelect-select': {
                          color: '#FFFFFF',
                          fontWeight: 600,
                          fontSize: '0.95rem',
                        },
                        '& .MuiSelect-icon': {
                          color: theme.palette.mode === 'dark' ? '#FF6B6B' : '#FF5252',
                          fontSize: '1.5rem',
                        }
                      }}
                    >
                      <InputLabel sx={{ 
                        background: theme.palette.mode === 'dark' 
                          ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)'
                          : 'linear-gradient(135deg, #FFFFFF 0%, #F8F8F8 100%)',
                        px: 1,
                        borderRadius: 1,
                      }}>
                        🎬 Filter by Era
                      </InputLabel>
                      <Select
                        value={selectedEra}
                        onChange={(e) => setSelectedEra(e.target.value)}
                        label="🎬 Filter by Era"
                        MenuProps={{
                          PaperProps: {
                            sx: {
                              bgcolor: theme.palette.mode === 'dark' 
                                ? 'linear-gradient(135deg, #1A1A1A 0%, #2A2A2A 100%)'
                                : 'linear-gradient(135deg, #FFFFFF 0%, #F8F8F8 100%)',
                              border: `1px solid ${theme.palette.mode === 'dark' 
                                ? 'rgba(255, 107, 107, 0.3)' 
                                : 'rgba(255, 82, 82, 0.2)'}`,
                              borderRadius: 2,
                              mt: 1,
                              boxShadow: theme.palette.mode === 'dark'
                                ? '0 12px 40px rgba(0, 0, 0, 0.8)'
                                : '0 12px 40px rgba(0, 0, 0, 0.15)',
                              '& .MuiMenuItem-root': {
                                color: '#FFFFFF',
                                fontWeight: 500,
                                transition: 'all 0.2s ease',
                                '&:hover': {
                                  bgcolor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 107, 107, 0.1)' 
                                    : 'rgba(255, 82, 82, 0.05)',
                                  transform: 'translateX(8px)',
                                },
                                '&.Mui-selected': {
                                  bgcolor: theme.palette.mode === 'dark' 
                                    ? 'rgba(255, 107, 107, 0.2)' 
                                    : 'rgba(255, 82, 82, 0.1)',
                                  color: theme.palette.mode === 'dark' ? '#FF6B6B' : '#FF5252',
                                  fontWeight: 700,
                                  '&:hover': {
                                    bgcolor: theme.palette.mode === 'dark' 
                                      ? 'rgba(255, 107, 107, 0.3)' 
                                      : 'rgba(255, 82, 82, 0.15)',
                                  }
                                }
                              }
                            }
                          }
                        }}
                      >
                        <MenuItem value="" sx={{ fontStyle: 'italic', opacity: 0.8 }}>
                          ✨ All Eras (Random Magic)
                        </MenuItem>
                        {availableEras.map((era) => (
                          <MenuItem key={era} value={era}>
                            🎥 {era}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box display="flex" justifyContent="center" gap={2} flexWrap="wrap">
                    <Tooltip title="Play/Pause (Space)" arrow placement="top">
                      <IconButton
                        onClick={handlePlayPause}
                        sx={{ 
                          background: theme.palette.mode === 'dark' 
                            ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
                            : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
                          color: '#FFFFFF',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          width: 48,
                          height: 48,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: theme.palette.mode === 'dark'
                              ? 'linear-gradient(135deg, #FF6B6B 0%, #FF5252 100%)'
                              : 'linear-gradient(135deg, #FF5252 0%, #E53935 100%)',
                            color: '#FFFFFF',
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 20px rgba(255, 107, 107, 0.4)'
                              : '0 8px 20px rgba(255, 82, 82, 0.4)',
                          }
                        }}
                      >
                        {isPlaying ? <Pause /> : <PlayArrow />}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Next Video (→ or N)" arrow placement="top">
                      <IconButton
                        onClick={playRandomVideo}
                        disabled={isLoading}
                        sx={{ 
                          background: theme.palette.mode === 'dark' 
                            ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
                            : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
                          color: '#FFFFFF',
                          border: `1px solid ${theme.palette.divider}`,
                          borderRadius: 2,
                          width: 48,
                          height: 48,
                          transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                          '&:hover': {
                            background: theme.palette.mode === 'dark'
                              ? 'linear-gradient(135deg, #FFFFFF 0%, #F0F0F0 100%)'
                              : 'linear-gradient(135deg, #000000 0%, #333333 100%)',
                            color: theme.palette.mode === 'dark' ? '#000000' : '#FFFFFF',
                            transform: 'translateY(-2px)',
                            boxShadow: theme.palette.mode === 'dark'
                              ? '0 8px 20px rgba(255, 255, 255, 0.3)'
                              : '0 8px 20px rgba(0, 0, 0, 0.3)',
                          },
                          '&:disabled': {
                            opacity: 0.4,
                            transform: 'none',
                          }
                        }}
                      >
                        <SkipNext />
                      </IconButton>
                    </Tooltip>
                    <SmartPlaylist
                      allVideoIds={allVideoIds}
                      onPlayVideo={handlePlaySpecificVideo}
                      currentVideoId={currentVideoId}
                      userPreferences={userPreferences}
                      onUpdatePreferences={handleUpdatePreferences}
                    />
                    <VideoGallery
                      allVideoIds={allVideoIds}
                      onPlayVideo={handlePlaySpecificVideo}
                      currentVideoId={currentVideoId}
                      videoStats={videoStats}
                    />
                    <Tooltip title="Help & Tips" arrow>
                      <IconButton 
                        onClick={() => setHelpOpen(true)}
                        sx={{ 
                          color: theme.palette.info.main, 
                          bgcolor: `${theme.palette.info.main}15`,
                          border: `1px solid ${theme.palette.info.main}30`,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: `${theme.palette.info.main}25`,
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <HelpOutline />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CleanCard>
                
                {/* Next Vlog Preview */}
                <Box sx={{ mt: 3, mb: 3 }}>
                  <NextVlogPreview
                    allVideoIds={allVideoIds}
                    currentVideoId={currentVideoId}
                    onPlayVideo={handlePlaySpecificVideo}
                    userPreferences={userPreferences}
                    autoRefresh={autoplay}
                    selectedEra={selectedEra}
                  />
                </Box>

                {/* Stats */}
                <StatsCard videoCount={videoCount} totalVideos={allVideoIds.length} playStats={playStats} />
              </Box>
                </div>
              </Slide>
            ) : (
              <Box>
                <CleanCard elevation={0} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ fontWeight: 700, color: theme.palette.text.primary, mb: 3 }}>
                    Quick Actions
                  </Typography>
                  
                  {/* Era Filter */}
                  <Box display="flex" justifyContent="center" mb={3}>
                    <FormControl variant="outlined" sx={{ minWidth: 200 }}>
                      <InputLabel>Filter by Era</InputLabel>
                      <Select
                        value={selectedEra}
                        onChange={(e) => setSelectedEra(e.target.value)}
                        label="Filter by Era"
                      >
                        <MenuItem value="">
                          <em>All Eras</em>
                        </MenuItem>
                        {availableEras.map((era) => (
                          <MenuItem key={era} value={era}>
                            {era}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                  
                  <Box display="flex" gap={2} justifyContent="center" flexWrap="wrap">
                    <Tooltip title="Skip to next video" arrow>
                      <IconButton 
                        onClick={playRandomVideo}
                        sx={{ 
                          color: theme.palette.primary.main, 
                          bgcolor: `${theme.palette.primary.main}15`,
                          border: `1px solid ${theme.palette.primary.main}30`,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: `${theme.palette.primary.main}25`,
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <SkipNext />
                      </IconButton>
                    </Tooltip>
                    <SmartPlaylist
                      allVideoIds={allVideoIds}
                      onPlayVideo={handlePlaySpecificVideo}
                      currentVideoId={currentVideoId}
                      userPreferences={userPreferences}
                      onUpdatePreferences={handleUpdatePreferences}
                    />
                    <VideoGallery
                      allVideoIds={allVideoIds}
                      onPlayVideo={handlePlaySpecificVideo}
                      currentVideoId={currentVideoId}
                      videoStats={videoStats}
                    />
                    <Tooltip title="Help & Tips" arrow>
                      <IconButton 
                        onClick={() => setHelpOpen(true)}
                        sx={{ 
                          color: theme.palette.info.main, 
                          bgcolor: `${theme.palette.info.main}15`,
                          border: `1px solid ${theme.palette.info.main}30`,
                          borderRadius: 2,
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            bgcolor: `${theme.palette.info.main}25`,
                            transform: 'translateY(-2px)',
                          }
                        }}
                      >
                        <HelpOutline />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CleanCard>
                
                {/* Next Vlog Preview */}
                <Box sx={{ mt: 3, mb: 3 }}>
                  <NextVlogPreview
                    allVideoIds={allVideoIds}
                    currentVideoId={currentVideoId}
                    onPlayVideo={handlePlaySpecificVideo}
                    userPreferences={userPreferences}
                    autoRefresh={autoplay}
                    selectedEra={selectedEra}
                  />
                </Box>

                <StatsCard videoCount={videoCount} totalVideos={allVideoIds.length} playStats={playStats} />
              </Box>
            )}
          </Grid>
        </Grid>

        {/* Help Dialog */}
        <Dialog
          open={helpOpen}
          onClose={() => setHelpOpen(false)}
          maxWidth="md"
          fullWidth
          PaperProps={{
            sx: {
              background: theme.palette.mode === 'dark'
                ? 'linear-gradient(135deg, #1A1A1A 0%, #0A0A0A 100%)'
                : 'linear-gradient(135deg, #FFFFFF 0%, #F8F9FA 100%)',
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 3,
              color: theme.palette.text.primary,
              boxShadow: theme.palette.mode === 'dark'
                ? '0 24px 64px rgba(0, 0, 0, 0.8)'
                : '0 24px 64px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <DialogTitle sx={{ 
            display: 'flex', 
            alignItems: 'center', 
            justifyContent: 'space-between',
            background: theme.palette.mode === 'dark'
              ? 'linear-gradient(135deg, #2A2A2A 0%, #1A1A1A 100%)'
              : 'linear-gradient(135deg, #F5F5F5 0%, #E0E0E0 100%)',
            color: theme.palette.text.primary,
            fontWeight: 600,
            borderBottom: `1px solid ${theme.palette.divider}`,
            py: 3
          }}>
            <Box display="flex" alignItems="center" gap={2}>
              <HelpOutline sx={{ 
                color: theme.palette.mode === 'dark' ? '#C0C0C0' : '#9E9E9E' 
              }} />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
              Help & Tips
              </Typography>
            </Box>
            <IconButton 
              onClick={() => setHelpOpen(false)} 
              sx={{ 
                color: theme.palette.text.secondary,
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark'
                    ? 'rgba(255, 255, 255, 0.1)'
                    : 'rgba(0, 0, 0, 0.04)',
                }
              }}
            >
              <Close />
            </IconButton>
          </DialogTitle>

          <DialogContent sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
              
              {/* Keyboard Controls Section */}
              <Box>
                <Typography variant="h6" gutterBottom fontWeight={600} 
                  sx={(theme) => ({
                    display: 'flex', 
                    alignItems: 'center', 
                    gap: 1, 
                    mb: 2,
                    color: theme.palette.text.primary
                  })}>
                  ⌨️ Keyboard Controls
                </Typography>
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 3 }}>
                  {/* Video Controls */}
                  <Box>
                    <Typography variant="subtitle2" sx={(theme) => ({ 
                      color: '#FFFFFF', 
                      fontWeight: 700, 
                      mb: 1 
                    })}>
                      🎬 Video Controls
                    </Typography>
                    <List dense sx={(theme) => ({ 
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 107, 107, 0.08)' 
                        : 'rgba(255, 82, 82, 0.03)', 
                      borderRadius: 2, 
                      p: 1.5,
                      border: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 107, 107, 0.15)' 
                        : 'rgba(255, 82, 82, 0.08)'}`
                    })}>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="Space" 
                          secondary="Play/Pause"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="→ or N" 
                          secondary="Next video"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="← or P" 
                          secondary="Previous video"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="R" 
                          secondary="Random video"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="F" 
                          secondary="Fullscreen"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                    </List>
                  </Box>

                  {/* App Controls */}
                  <Box>
                    <Typography variant="subtitle2" sx={(theme) => ({ 
                      color: '#FFFFFF', 
                      fontWeight: 700, 
                      mb: 1 
                    })}>
                      ⚙️ App Controls
                    </Typography>
                    <List dense sx={(theme) => ({ 
                      bgcolor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 107, 107, 0.08)' 
                        : 'rgba(255, 82, 82, 0.03)', 
                      borderRadius: 2, 
                      p: 1.5,
                      border: `1px solid ${theme.palette.mode === 'dark' 
                        ? 'rgba(255, 107, 107, 0.15)' 
                        : 'rgba(255, 82, 82, 0.08)'}`
                    })}>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="T" 
                          secondary="Toggle theme"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="A" 
                          secondary="Toggle autoplay"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="H or F1" 
                          secondary="Show help"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="G" 
                          secondary="Video gallery"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.5 }}>
                        <ListItemText 
                          primary="Esc" 
                          secondary="Close dialogs"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.9rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.8rem', color: 'inherit' }}
                        />
                      </ListItem>
                    </List>
                  </Box>
                </Box>

                {/* Additional Shortcuts */}
                <Box sx={{ mt: 2 }}>
                  <Typography variant="subtitle2" sx={(theme) => ({ 
                    color: '#FFFFFF', 
                    fontWeight: 700, 
                    mb: 1 
                  })}>
                    🎵 Audio & More
                  </Typography>
                  <List dense sx={(theme) => ({ 
                    bgcolor: theme.palette.mode === 'dark' 
                      ? 'rgba(255, 107, 107, 0.08)' 
                      : 'rgba(255, 82, 82, 0.03)', 
                    borderRadius: 2, 
                    p: 1.5,
                    border: `1px solid ${theme.palette.mode === 'dark' 
                      ? 'rgba(255, 107, 107, 0.15)' 
                      : 'rgba(255, 82, 82, 0.08)'}`
                  })}>
                    <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 1 }}>
                      <ListItem sx={{ py: 0.3, px: 1 }}>
                        <ListItemText 
                          primary="↑/↓" 
                          secondary="Volume"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.3, px: 1 }}>
                        <ListItemText 
                          primary="M" 
                          secondary="Mute"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.3, px: 1 }}>
                        <ListItemText 
                          primary="S" 
                          secondary="Smart Playlist"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: 'inherit' }}
                        />
                      </ListItem>
                      <ListItem sx={{ py: 0.3, px: 1 }}>
                        <ListItemText 
                          primary="1-5" 
                          secondary="Quick select"
                          primaryTypographyProps={{ fontWeight: 600, fontSize: '0.85rem', color: 'inherit' }}
                          secondaryTypographyProps={{ fontSize: '0.75rem', color: 'inherit' }}
                        />
                      </ListItem>
                    </Box>
                  </List>
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions sx={{ p: 3, pt: 0 }}>
            <Button
              variant="contained"
              onClick={() => setHelpOpen(false)}
            >
              Got it!
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </BackgroundContainer>
  );
}

function App() {
  const [darkMode, setDarkMode] = useState(true); // Default to dark mode
  
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
  };

  const theme = createAppTheme(darkMode ? 'dark' : 'light');

  return (
    <ThemeProvider theme={theme}>
      <ThemeContext.Provider value={{ darkMode, toggleDarkMode }}>
        <AppContent />
      </ThemeContext.Provider>
    </ThemeProvider>
  );
}

export default App;